import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Star } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORIES = [
  { value: "", label: "Tout" },
  { value: "laptop", label: "Ordinateurs" },
  { value: "tablet", label: "Tablettes" },
  { value: "phone", label: "Téléphones" },
  { value: "monitor", label: "Écrans" },
];

export default function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = category ? { category } : {};
        const { data } = await api.get("/products", { params });
        setProducts(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category]);

  return (
    <div className="space-y-10" data-testid="catalog-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-2">Catalogue</div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white leading-none">
            Tech reconditionnée,<br />sélectionnée à la main.
          </h1>
          <p className="mt-4 text-sm text-brand-gray font-roboto max-w-xl">
            Diagnostic 50 points · garantie 12 mois · expédition France.
          </p>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c.value || "all"}
              onClick={() => setCategory(c.value)}
              data-testid={`filter-${c.value || "all"}`}
              className={`px-4 h-10 text-xs font-bold uppercase tracking-[0.18em] rounded-sm border transition-colors ${
                category === c.value
                  ? "bg-brand-green text-black border-brand-green"
                  : "bg-transparent text-white border-brand-blue/60 hover:border-brand-green/70"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-brand-green" />
        </div>
      ) : products.length === 0 ? (
        <div className="py-20 text-center text-brand-gray font-roboto" data-testid="catalog-empty">
          Aucun produit trouvé.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="products-grid">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} delay={i * 0.04} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, delay = 0 }) {
  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/app/products/${product.id}`} className="group block">
        <div className="relative aspect-square overflow-hidden bg-brand-navy/40 border border-brand-blue/40 rounded-sm">
          {product.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-brand-gray/40 text-sm">
              Aucune image
            </div>
          )}
          {product.featured && (
            <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 bg-brand-green text-black text-[10px] font-bold uppercase tracking-wider rounded-sm">
              <Star className="h-3 w-3 fill-black" /> Top
            </div>
          )}
          {discount > 0 && (
            <div className="absolute top-3 right-3 px-2 py-1 bg-black/80 backdrop-blur text-brand-green text-[10px] font-bold uppercase tracking-wider rounded-sm border border-brand-green/40">
              -{discount}%
            </div>
          )}
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-gray/70">
              {product.brand}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-green">
              Grade {product.grade}
            </span>
          </div>
          <h3 className="mt-2 font-bold text-white leading-tight line-clamp-2 min-h-[2.6em] group-hover:text-brand-green transition">
            {product.name}
          </h3>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-black text-xl text-white tracking-tight">{product.price.toFixed(0)} €</span>
            {product.original_price && (
              <span className="text-xs line-through text-brand-gray/50">{product.original_price.toFixed(0)} €</span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
